import type { TableFilterApi } from '../types/config-table.api.types';
import type { ConfigTableQuery } from '../types/config-table.types';
import type { GpcCategoryApi } from '../types/gpc-categories.api.types';
import type { GpcCategory, GpcCategoryFilters } from '../types/gpc-categories.types';

/** Maps one wire row to its camelCase domain equivalent. */
export function toGpcCategory(api: GpcCategoryApi): GpcCategory {
  return {
    gpcId: api.gpc_id,
    locale: api.locale,
    gpcPath: api.gpc_path,
    categoryName: api.category_name,
    depth: api.depth,
    parentGpcId: api.parent_gpc_id,
    taxonomyVersion: api.taxonomy_version,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/** `gpc_id`, `parent_gpc_id` and `depth` are all Postgres `integer` (int4). A value
 *  outside this range is not a query that matches nothing — agentic-config's
 *  `_coerce` raises `TableQueryError`, which the route answers with a 400. */
const INT4_MIN = -2147483648;
const INT4_MAX = 2147483647;

/**
 * Parses a digits-only filter field to the integer the wire wants.
 *
 * Returns `null` for `''`, for anything that doesn't parse, **and for anything the
 * target int4 column cannot hold**. That last case is the reachable one: the inputs
 * admit digits but not a magnitude, so pasting a 13-digit barcode into GPC ID would
 * otherwise send `9780306406157` and collapse the whole grid into its error state —
 * with nothing on screen naming the filter as the cause. Dropped here instead, so an
 * over-long id reads as "no such id", which is what the input's own digit-stripping
 * promises.
 *
 * Sent as a number rather than the raw string even though the backend coerces either:
 * the coercion is a documented convenience, not something to lean on.
 */
function toIntFilter(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) return null;
  return parsed >= INT4_MIN && parsed <= INT4_MAX ? parsed : null;
}

/**
 * Maps page filter state to a `/tables/rows` query — and, unchanged, to the export
 * body, which is what guarantees the downloaded file holds exactly the rows on
 * screen.
 *
 * Inactive filters contribute no condition at all, rather than a condition matching
 * the `'ALL'`/`''` sentinel — the endpoint has no concept of those, so sending one
 * would filter for the literal string.
 *
 * Operator per column is chosen from what that column actually accepts (each
 * column's `operators` in `/tables/columns`), not assumed:
 * - `locale` is an enum and rejects `contains` outright, so it uses `eq`. Its values
 *   are validated case-sensitively server-side, which is why the vocabulary is
 *   carried through verbatim (`en-US`, never `en-us`).
 * - `depth`, `gpc_id` and `parent_gpc_id` are integers: `eq` against a parsed number.
 * - `gpc_path` uses `contains`, which is what makes a pasted ancestor match its whole
 *   subtree — the main thing this table is asked. LIKE wildcards are escaped
 *   server-side, so `%` and `_` in a path match themselves.
 * - `taxonomy_version` is a plain string column with a distinct facet, so `eq`.
 */
export function toGpcCategoryQueryApi(filters: GpcCategoryFilters): ConfigTableQuery {
  const conditions: TableFilterApi[] = [];

  if (filters.locale !== 'ALL') {
    conditions.push({ column: 'locale', op: 'eq', value: filters.locale });
  }

  const depth = toIntFilter(filters.depth === 'ALL' ? '' : filters.depth);
  if (depth !== null) {
    conditions.push({ column: 'depth', op: 'eq', value: depth });
  }

  if (filters.taxonomyVersion !== 'ALL') {
    conditions.push({ column: 'taxonomy_version', op: 'eq', value: filters.taxonomyVersion });
  }

  const gpcPath = filters.gpcPath.trim();
  if (gpcPath.length > 0) {
    conditions.push({ column: 'gpc_path', op: 'contains', value: gpcPath });
  }

  const gpcId = toIntFilter(filters.gpcId);
  if (gpcId !== null) {
    conditions.push({ column: 'gpc_id', op: 'eq', value: gpcId });
  }

  const parentGpcId = toIntFilter(filters.parentGpcId);
  if (parentGpcId !== null) {
    conditions.push({ column: 'parent_gpc_id', op: 'eq', value: parentGpcId });
  }

  const search = filters.search.trim();
  return search.length > 0 ? { filters: conditions, search } : { filters: conditions };
}
